import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from '../../components/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ScatterChart, Scatter, Cell, Legend, FunnelChart, Funnel, LabelList
} from 'recharts';
import { 
  Users, Shield, Camera, Activity, AlertTriangle, 
  CheckCircle2, TrendingUp, TrendingDown, Award
} from 'lucide-react';
import THRDistrictDetailModal from '../../components/THRDistrictDetailModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FRSAuthDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // State for all data
  const [stateOverview, setStateOverview] = useState(null);
  const [districtCoverage, setDistrictCoverage] = useState(null);
  const [thrFRS, setThrFRS] = useState(null);
  const [freshCapture, setFreshCapture] = useState(null);
  const [kitPerformance, setKitPerformance] = useState(null);
  const [quadrantAnalysis, setQuadrantAnalysis] = useState(null);
  const [redFlags, setRedFlags] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Custom scatter shape component for clickable points
  const ClickableScatterCell = (props) => {
    const { cx, cy, payload } = props;
    const [isHovered, setIsHovered] = React.useState(false);
    
    return (
      <circle
        cx={cx}
        cy={cy}
        r={isHovered ? 8 : 6}
        fill={isHovered ? '#2563eb' : '#3b82f6'}
        style={{ cursor: 'pointer', transition: 'all 0.2s' }}
        onClick={() => {
          if (payload?.district) {
            setSelectedDistrict({ district: payload.district, district_id: payload.district });
            setIsModalOpen(true);
          }
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      />
    );
  };

  // Custom bar shape component for clickable bars
  const ClickableBarShape = (props) => {
    const { payload, x, y, width, height } = props;
    const [isHovered, setIsHovered] = React.useState(false);
    
    const handleClick = (e) => {
      e.stopPropagation();
      if (payload?.district_id) {
        setSelectedDistrict({ district: payload.district_id, district_id: payload.district_id });
        setIsModalOpen(true);
      }
    };
    
    // Determine color based on productivity value
    const productivity = payload?.enrol_upd_per_kit_per_day || 0;
    let baseFill = '#ef4444'; // red (low)
    if (productivity >= 12) baseFill = '#22c55e'; // green (high)
    else if (productivity >= 8) baseFill = '#f59e0b'; // orange (medium)
    
    // Hover color
    const hoverFill = isHovered ? (
      baseFill === '#22c55e' ? '#16a34a' : 
      baseFill === '#f59e0b' ? '#d97706' : 
      '#dc2626'
    ) : baseFill;
    
    return (
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={hoverFill}
        style={{ cursor: 'pointer', transition: 'all 0.2s', opacity: isHovered ? 0.8 : 1 }}
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      />
    );
  };

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      setLoading(true);
      
      const [overview, coverage, thr, capture, kit, quadrant, flags] = await Promise.all([
        axios.get(`${API_URL}/api/dashboards/frs-auth/state-overview`),
        axios.get(`${API_URL}/api/dashboards/frs-auth/frs-district-coverage`),
        axios.get(`${API_URL}/api/dashboards/frs-auth/thr-frs-authentication`),
        axios.get(`${API_URL}/api/dashboards/frs-auth/fresh-capture-analysis`),
        axios.get(`${API_URL}/api/dashboards/frs-auth/aadhaar-kit-performance`),
        axios.get(`${API_URL}/api/dashboards/frs-auth/combined-quadrant-analysis`),
        axios.get(`${API_URL}/api/dashboards/frs-auth/red-flag-districts`)
      ]);

      setStateOverview(overview.data);
      setDistrictCoverage(coverage.data);
      setThrFRS(thr.data);
      setFreshCapture(capture.data);
      setKitPerformance(kit.data);
      setQuadrantAnalysis(quadrant.data);
      setRedFlags(flags.data);
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading FRS authentication data:', error);
      setLoading(false);
    }
  };

  // Reusable KPI Card Component
  const KPICard = ({ title, value, unit, icon: Icon, color, subtitle, trend }) => (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between mb-4">
          <div className={`p-3 rounded-full`} style={{ backgroundColor: `${color}20` }}>
            <Icon className="w-6 h-6" style={{ color }} />
          </div>
          {trend && (
            <div className={`flex items-center gap-1 text-sm ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
              {trend > 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
              {Math.abs(trend)}%
            </div>
          )}
        </div>
        <h3 className="text-sm font-medium text-gray-600 mb-2">{title}</h3>
        <p className="text-3xl font-bold" style={{ color }}>{value}{unit}</p>
        {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
      </CardContent>
    </Card>
  );

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading FRS Authentication Dashboard...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // PAGE 1: State Overview
  const OverviewTab = () => {
    if (!stateOverview) return <div>No data available</div>;

    const funnelData = [
      { 
        name: 'Eligible Beneficiaries', 
        value: stateOverview.frs_metrics.total_eligible,
        fill: '#3b82f6'
      },
      { 
        name: 'Aadhaar Face Matched', 
        value: stateOverview.frs_metrics.aadhaar_face_matched,
        fill: '#22c55e'
      },
      { 
        name: 'THR with FRS Auth', 
        value: stateOverview.frs_metrics.face_matched_thr,
        fill: '#f59e0b'
      }
    ];

    return (
      <div className="space-y-6">
        {/* Top KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <KPICard
            title="Total Eligible Beneficiaries"
            value={stateOverview.frs_metrics.total_eligible.toLocaleString()}
            unit=""
            icon={Users}
            color="#3b82f6"
            subtitle={`${stateOverview.total_districts} districts`}
          />
          <KPICard
            title="Face Match Coverage"
            value={stateOverview.frs_metrics.face_match_pct}
            unit="%"
            icon={Shield}
            color="#22c55e"
            subtitle={`${stateOverview.frs_metrics.aadhaar_face_matched.toLocaleString()} beneficiaries`}
          />
          <KPICard
            title="THR-FRS Coverage"
            value={stateOverview.frs_metrics.face_matched_thr_pct}
            unit="%"
            icon={CheckCircle2}
            color="#f59e0b"
            subtitle={`${stateOverview.frs_metrics.face_matched_thr.toLocaleString()} authenticated`}
          />
        </div>

        {/* Second Row KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <KPICard
            title="Fresh FRS Captures"
            value={stateOverview.frs_metrics.face_captured_thr_pct}
            unit="%"
            icon={Camera}
            color="#8b5cf6"
            subtitle={`${stateOverview.frs_metrics.face_captured_thr.toLocaleString()} new captures`}
          />
          <KPICard
            title="Active Aadhaar Kits"
            value={stateOverview.aadhaar_metrics.active_kits}
            unit=""
            icon={Activity}
            color="#06b6d4"
            subtitle={`${stateOverview.aadhaar_metrics.allocated_kits} allocated`}
          />
          <KPICard
            title="Avg Kit Productivity"
            value={stateOverview.aadhaar_metrics.avg_kit_productivity}
            unit="/day"
            icon={TrendingUp}
            color="#10b981"
            subtitle={`${stateOverview.aadhaar_metrics.total_enrol_upd.toLocaleString()} total operations`}
          />
        </div>

        {/* Funnel Chart */}
        <Card>
          <CardHeader>
            <CardTitle>FRS Authentication Funnel</CardTitle>
            <CardDescription>Beneficiary journey from eligibility to authenticated THR distribution</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ResponsiveContainer width="100%" height={300}>
                <FunnelChart>
                  <Tooltip />
                  <Funnel dataKey="value" data={funnelData}>
                    <LabelList position="right" fill="#000" stroke="none" dataKey="name" />
                  </Funnel>
                </FunnelChart>
              </ResponsiveContainer>
              
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm font-semibold text-blue-900">Stage 1: Eligible</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {stateOverview.frs_metrics.total_eligible.toLocaleString()}
                  </p>
                  <p className="text-xs text-blue-700 mt-1">Total beneficiary pool</p>
                </div>
                
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-sm font-semibold text-green-900">Stage 2: Aadhaar Matched</p>
                  <p className="text-2xl font-bold text-green-600">
                    {stateOverview.frs_metrics.aadhaar_face_matched.toLocaleString()}
                  </p>
                  <p className="text-xs text-green-700 mt-1">
                    Drop-off: {(stateOverview.frs_metrics.total_eligible - stateOverview.frs_metrics.aadhaar_face_matched).toLocaleString()}
                  </p>
                </div>
                
                <div className="p-4 bg-orange-50 rounded-lg">
                  <p className="text-sm font-semibold text-orange-900">Stage 3: THR with FRS</p>
                  <p className="text-2xl font-bold text-orange-600">
                    {stateOverview.frs_metrics.face_matched_thr.toLocaleString()}
                  </p>
                  <p className="text-xs text-orange-700 mt-1">
                    Gap: {(stateOverview.frs_metrics.aadhaar_face_matched - stateOverview.frs_metrics.face_matched_thr).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* District Coverage Chart */}
        {districtCoverage && (
          <Card>
            <CardHeader>
              <CardTitle>FRS Coverage by District</CardTitle>
              <CardDescription>Aadhaar face match coverage (lowest first)</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart 
                  data={districtCoverage.all_districts.slice(0, 20)}
                  margin={{ top: 20, right: 30, left: 20, bottom: 100 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="district_id" 
                    angle={-45} 
                    textAnchor="end" 
                    height={100}
                    tick={{ fontSize: 10 }}
                    interval={0}
                  />
                  <YAxis 
                    domain={[0, 100]} 
                    tick={{ fontSize: 12 }}
                    width={60}
                  />
                  <Tooltip 
                    contentStyle={{ fontSize: '12px' }}
                  />
                  <Bar dataKey="face_match_pct" radius={[8, 8, 0, 0]}>
                    {districtCoverage.all_districts.slice(0, 20).map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={entry.face_match_pct >= 90 ? '#22c55e' : entry.face_match_pct >= 80 ? '#f59e0b' : '#ef4444'} 
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* THR-FRS Authentication by District */}
        {thrFRS && (
          <Card>
            <CardHeader>
              <CardTitle>THR Distribution with FRS Authentication</CardTitle>
              <CardDescription>Districts sorted by THR-FRS coverage (lowest first)</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart 
                  data={thrFRS.all_districts.slice(0, 20)}
                  margin={{ top: 20, right: 30, left: 20, bottom: 100 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="district_id" 
                    angle={-45} 
                    textAnchor="end" 
                    height={100}
                    tick={{ fontSize: 10 }}
                    interval={0}
                  />
                  <YAxis 
                    domain={[0, 100]} 
                    tick={{ fontSize: 12 }}
                    width={60}
                  />
                  <Tooltip 
                    contentStyle={{ fontSize: '12px' }}
                  />
                  <Bar dataKey="face_matched_thr_pct" radius={[8, 8, 0, 0]}>
                    {thrFRS.all_districts.slice(0, 20).map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={entry.face_matched_thr_pct >= 75 ? '#22c55e' : entry.face_matched_thr_pct >= 50 ? '#f59e0b' : '#ef4444'} 
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  // PAGE 2: FRS System Deep Dive
  const FRSDeepDiveTab = () => {
    if (!freshCapture || !thrFRS) return <div>No data available</div>;

    return (
      <div className="space-y-6">
        {/* Fresh Capture vs THR-FRS Scatter */}
        <Card>
          <CardHeader>
            <CardTitle>Fresh Capture vs THR-FRS Authentication</CardTitle>
            <CardDescription>District positioning on capture and authentication dimensions</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={400}>
              <ScatterChart>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  type="number" 
                  dataKey="fresh_capture_pct" 
                  name="Fresh Capture %" 
                  domain={[0, 30]}
                  label={{ value: 'Fresh Capture %', position: 'insideBottom', offset: -5 }}
                />
                <YAxis 
                  type="number" 
                  dataKey="thr_frs_pct" 
                  name="THR-FRS %" 
                  domain={[0, 100]}
                  label={{ value: 'THR-FRS %', angle: -90, position: 'insideLeft' }}
                />
                <Tooltip 
                  cursor={{ strokeDasharray: '3 3' }}
                  content={({ active, payload }) => {
                    if (active && payload && payload[0]) {
                      const data = payload[0].payload;
                      return (
                        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
                          <p className="font-semibold text-blue-600">{data.district}</p>
                          <p className="text-sm text-gray-600">Fresh Capture: {data.fresh_capture_pct}%</p>
                          <p className="text-sm text-gray-600">THR-FRS: {data.thr_frs_pct}%</p>
                          <p className="text-xs text-gray-500 mt-1">Click to view details</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Legend />
                <Scatter 
                  name="Districts" 
                  data={freshCapture.scatter_data} 
                  fill="#3b82f6"
                  shape={ClickableScatterCell}
                />
              </ScatterChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Top Performers */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-green-600">Top 10 THR-FRS Districts</CardTitle>
              <CardDescription>Highest THR distribution with FRS authentication</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {thrFRS.top_10.map((district, idx) => (
                  <div 
                    key={idx} 
                    className="flex justify-between items-center p-3 bg-green-50 rounded-lg cursor-pointer hover:bg-green-100"
                    onClick={() => {
                      setSelectedDistrict({ district: district.district_id, district_id: district.district_id });
                      setIsModalOpen(true);
                    }}
                  >
                    <div>
                      <p className="font-semibold text-green-900">{district.district_id}</p>
                      <p className="text-xs text-green-700">
                        {district.face_matched_thr.toLocaleString()} / {district.total_eligible.toLocaleString()}
                      </p>
                    </div>
                    <p className="text-2xl font-bold text-green-600">{district.face_matched_thr_pct}%</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-red-600">Bottom 10 THR-FRS Districts</CardTitle>
              <CardDescription>Critical districts needing immediate intervention</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {thrFRS.bottom_10.map((district, idx) => (
                  <div 
                    key={idx} 
                    className="flex justify-between items-center p-3 bg-red-50 rounded-lg cursor-pointer hover:bg-red-100"
                    onClick={() => {
                      setSelectedDistrict({ district: district.district_id, district_id: district.district_id });
                      setIsModalOpen(true);
                    }}
                  >
                    <div>
                      <p className="font-semibold text-red-900">{district.district_id}</p>
                      <p className="text-xs text-red-700">
                        {district.face_matched_thr.toLocaleString()} / {district.total_eligible.toLocaleString()}
                      </p>
                    </div>
                    <p className="text-2xl font-bold text-red-600">{district.face_matched_thr_pct}%</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Top Fresh Capture Districts */}
        <Card>
          <CardHeader>
            <CardTitle>Top Fresh Capture Districts</CardTitle>
            <CardDescription>Districts aggressively using FRS for new beneficiary captures</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {freshCapture.top_fresh_capture.slice(0, 10).map((district, idx) => (
                <div key={idx} className="p-4 bg-purple-50 rounded-lg text-center">
                  <p className="font-semibold text-sm text-purple-900">{district.district_id}</p>
                  <p className="text-2xl font-bold text-purple-600 mt-2">
                    {district.face_captured_thr_pct}%
                  </p>
                  <p className="text-xs text-purple-700 mt-1">
                    {district.face_captured_thr.toLocaleString()} captures
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // PAGE 3: Aadhaar Kit Performance
  const KitPerformanceTab = () => {
    if (!kitPerformance) return <div>No data available</div>;

    return (
      <div className="space-y-6">
        {/* Performance Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="border-green-500 border-2">
            <CardContent className="pt-6 text-center">
              <Award className="w-12 h-12 text-green-600 mx-auto mb-3" />
              <p className="text-3xl font-bold text-green-600">{kitPerformance.high_productivity.length}</p>
              <p className="text-sm text-gray-600 mt-1">High Productivity Districts</p>
              <p className="text-xs text-green-600 mt-2">≥12 operations/day/kit</p>
            </CardContent>
          </Card>

          <Card className="border-orange-500 border-2">
            <CardContent className="pt-6 text-center">
              <Activity className="w-12 h-12 text-orange-600 mx-auto mb-3" />
              <p className="text-3xl font-bold text-orange-600">{kitPerformance.medium_productivity.length}</p>
              <p className="text-sm text-gray-600 mt-1">Medium Productivity Districts</p>
              <p className="text-xs text-orange-600 mt-2">8-12 operations/day/kit</p>
            </CardContent>
          </Card>

          <Card className="border-red-500 border-2">
            <CardContent className="pt-6 text-center">
              <AlertTriangle className="w-12 h-12 text-red-600 mx-auto mb-3" />
              <p className="text-3xl font-bold text-red-600">{kitPerformance.low_productivity.length}</p>
              <p className="text-sm text-gray-600 mt-1">Low Productivity Districts</p>
              <p className="text-xs text-red-600 mt-2">&lt;8 operations/day/kit</p>
            </CardContent>
          </Card>
        </div>

        {/* Kit Productivity Chart */}
        <Card>
          <CardHeader>
            <CardTitle>District Kit Productivity</CardTitle>
            <CardDescription>Enrolment & updation per kit per day (sorted lowest first)</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={500}>
              <BarChart 
                data={kitPerformance.all_districts} 
                layout="vertical"
                margin={{ left: 120 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" domain={[0, 25]} />
                <YAxis dataKey="district_id" type="category" />
                <Tooltip 
                  content={({ active, payload }) => {
                    if (active && payload && payload[0]) {
                      const data = payload[0].payload;
                      return (
                        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
                          <p className="font-semibold text-blue-600">{data.district_id}</p>
                          <p className="text-sm text-gray-600">Productivity: {data.enrol_upd_per_kit_per_day} ops/day/kit</p>
                          {data.active_kits && <p className="text-sm text-gray-600">Active Kits: {data.active_kits}</p>}
                          <p className="text-xs text-gray-500 mt-1">Click to view details</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Bar 
                  dataKey="enrol_upd_per_kit_per_day" 
                  radius={[0, 8, 8, 0]}
                  shape={ClickableBarShape}
                  cursor="pointer"
                />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Top and Bottom 5 Tables */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="border-green-500 border-2">
            <CardHeader>
              <CardTitle className="text-green-600">Top 5 Kit Performers</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-green-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">District</th>
                      <th className="px-4 py-3 text-center font-semibold">Active Kits</th>
                      <th className="px-4 py-3 text-center font-semibold">Productivity</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {kitPerformance.top_5.map((district, idx) => (
                      <tr 
                        key={idx} 
                        className="hover:bg-green-50 cursor-pointer"
                        onClick={() => {
                          setSelectedDistrict({ district: district.district_id, district_id: district.district_id });
                          setIsModalOpen(true);
                        }}
                      >
                        <td className="px-4 py-3 font-medium">{district.district_id}</td>
                        <td className="px-4 py-3 text-center">{district.active_kits}</td>
                        <td className="px-4 py-3 text-center font-bold text-green-600">
                          {district.enrol_upd_per_kit_per_day}/day
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <Card className="border-red-500 border-2">
            <CardHeader>
              <CardTitle className="text-red-600">Bottom 5 Kit Performers</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-red-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">District</th>
                      <th className="px-4 py-3 text-center font-semibold">Active Kits</th>
                      <th className="px-4 py-3 text-center font-semibold">Productivity</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {kitPerformance.bottom_5.map((district, idx) => (
                      <tr 
                        key={idx} 
                        className="hover:bg-red-50 cursor-pointer"
                        onClick={() => {
                          setSelectedDistrict({ district: district.district_id, district_id: district.district_id });
                          setIsModalOpen(true);
                        }}
                      >
                        <td className="px-4 py-3 font-medium">{district.district_id}</td>
                        <td className="px-4 py-3 text-center">{district.active_kits}</td>
                        <td className="px-4 py-3 text-center font-bold text-red-600">
                          {district.enrol_upd_per_kit_per_day}/day
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  };

  // PAGE 4: Red Flag & Action View
  const RedFlagTab = () => {
    if (!quadrantAnalysis || !redFlags) return <div>No data available</div>;

    return (
      <div className="space-y-6">
        {/* Quadrant Summary */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="border-green-500 border-2">
            <CardContent className="pt-6 text-center">
              <Award className="w-10 h-10 text-green-600 mx-auto mb-2" />
              <p className="text-3xl font-bold text-green-600">{quadrantAnalysis.q1_champions.length}</p>
              <p className="text-sm text-gray-600 mt-1">Champions</p>
              <p className="text-xs text-green-600 mt-2">High THR-FRS + High Kit</p>
            </CardContent>
          </Card>

          <Card className="border-orange-500 border-2">
            <CardContent className="pt-6 text-center">
              <Activity className="w-10 h-10 text-orange-600 mx-auto mb-2" />
              <p className="text-3xl font-bold text-orange-600">{quadrantAnalysis.q2_good_thr_low_kit.length}</p>
              <p className="text-sm text-gray-600 mt-1">Good THR, Low Kit</p>
              <p className="text-xs text-orange-600 mt-2">Need kit optimization</p>
            </CardContent>
          </Card>

          <Card className="border-purple-500 border-2">
            <CardContent className="pt-6 text-center">
              <TrendingUp className="w-10 h-10 text-purple-600 mx-auto mb-2" />
              <p className="text-3xl font-bold text-purple-600">{quadrantAnalysis.q3_low_thr_good_kit.length}</p>
              <p className="text-sm text-gray-600 mt-1">Low THR, Good Kit</p>
              <p className="text-xs text-purple-600 mt-2">Need FRS at THR</p>
            </CardContent>
          </Card>

          <Card className="border-red-500 border-2">
            <CardContent className="pt-6 text-center">
              <AlertTriangle className="w-10 h-10 text-red-600 mx-auto mb-2" />
              <p className="text-3xl font-bold text-red-600">{quadrantAnalysis.q4_double_risk.length}</p>
              <p className="text-sm text-gray-600 mt-1">Double Risk</p>
              <p className="text-xs text-red-600 mt-2">Critical intervention</p>
            </CardContent>
          </Card>
        </div>

        {/* Quadrant Scatter Plot */}
        <Card>
          <CardHeader>
            <CardTitle>FRS-THR % vs Aadhaar Kit Productivity</CardTitle>
            <CardDescription>District positioning on combined performance dimensions</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={400}>
              <ScatterChart>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  type="number" 
                  dataKey="kit_productivity" 
                  name="Kit Productivity" 
                  domain={[0, 25]}
                  label={{ value: 'Kit Productivity (ops/day)', position: 'insideBottom', offset: -5 }}
                />
                <YAxis 
                  type="number" 
                  dataKey="thr_frs_pct" 
                  name="THR-FRS %" 
                  domain={[0, 100]}
                  label={{ value: 'THR-FRS %', angle: -90, position: 'insideLeft' }}
                />
                <Tooltip 
                  cursor={{ strokeDasharray: '3 3' }}
                  content={({ active, payload }) => {
                    if (active && payload && payload[0]) {
                      const data = payload[0].payload;
                      return (
                        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
                          <p className="font-semibold text-blue-600">{data.district}</p>
                          <p className="text-sm text-gray-600">THR-FRS: {data.thr_frs_pct}%</p>
                          <p className="text-sm text-gray-600">Kit Productivity: {data.kit_productivity} ops/day</p>
                          <p className="text-xs text-gray-500 mt-1">Click to view details</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Scatter 
                  name="Districts" 
                  data={quadrantAnalysis.combined_data} 
                  fill="#3b82f6"
                  shape={ClickableScatterCell}
                />
              </ScatterChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Champion Districts */}
        <Card className="border-green-500 border-2">
          <CardHeader>
            <CardTitle className="text-green-600">Champion Districts (Q1)</CardTitle>
            <CardDescription>Model districts with high THR-FRS and high kit productivity</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {quadrantAnalysis.q1_champions.map((district, idx) => (
                <div 
                  key={idx} 
                  className="p-4 bg-green-50 rounded-lg text-center cursor-pointer hover:bg-green-100 transition-colors"
                  onClick={() => {
                    setSelectedDistrict({ district: district.district, district_id: district.district });
                    setIsModalOpen(true);
                  }}
                >
                  <p className="font-semibold text-green-900">{district.district}</p>
                  <p className="text-sm text-green-700 mt-2">
                    THR-FRS: {district.thr_frs_pct}%
                  </p>
                  <p className="text-sm text-green-700">
                    Kit: {district.kit_productivity}/day
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Double Risk Districts */}
        <Card className="border-red-500 border-2">
          <CardHeader>
            <CardTitle className="text-red-600">Double Risk Districts (Q4)</CardTitle>
            <CardDescription>Critical districts requiring immediate comprehensive intervention</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-red-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">District</th>
                    <th className="px-4 py-3 text-center font-semibold">THR-FRS %</th>
                    <th className="px-4 py-3 text-center font-semibold">Face Match %</th>
                    <th className="px-4 py-3 text-center font-semibold">Kit Productivity</th>
                    <th className="px-4 py-3 text-left font-semibold">Priority</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {quadrantAnalysis.q4_double_risk.map((district, idx) => (
                    <tr 
                      key={idx} 
                      className="hover:bg-red-50 cursor-pointer"
                      onClick={() => {
                        setSelectedDistrict({ district: district.district, district_id: district.district });
                        setIsModalOpen(true);
                      }}
                    >
                      <td className="px-4 py-3 font-medium">{district.district}</td>
                      <td className="px-4 py-3 text-center font-bold text-red-600">
                        {district.thr_frs_pct}%
                      </td>
                      <td className="px-4 py-3 text-center">{district.face_match_pct}%</td>
                      <td className="px-4 py-3 text-center">{district.kit_productivity}/day</td>
                      <td className="px-4 py-3">
                        <span className="bg-red-600 text-white px-2 py-1 rounded text-xs font-semibold">
                          CRITICAL
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Red Flag Summary */}
        <Card className="border-orange-500 border-2">
          <CardHeader>
            <CardTitle className="text-orange-600">Priority Action Recommendations</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="p-4 bg-red-50 rounded-lg">
                <h4 className="font-semibold text-red-900 mb-2">
                  🚨 {redFlags.double_red_flags.length} Districts Need Immediate Dual Intervention
                </h4>
                <p className="text-sm text-red-700 mb-2">
                  Districts: {redFlags.double_red_flags.map(d => d.district).join(', ')}
                </p>
                <p className="text-sm text-red-800">
                  Action: Boost both FRS-linked THR delivery AND Aadhaar kit utilization
                </p>
              </div>

              <div className="p-4 bg-orange-50 rounded-lg">
                <h4 className="font-semibold text-orange-900 mb-2">
                  ⚠️ {redFlags.frs_red_flags.length} Districts with Low FRS Usage at THR
                </h4>
                <p className="text-sm text-orange-800">
                  Action: Training and supervision for FRS device usage during THR distribution
                </p>
              </div>

              <div className="p-4 bg-yellow-50 rounded-lg">
                <h4 className="font-semibold text-yellow-900 mb-2">
                  📋 {redFlags.kit_red_flags.length} Districts with Low Kit Productivity
                </h4>
                <p className="text-sm text-yellow-800">
                  Action: Review kit allocation, staffing, and mobilization strategies
                </p>
              </div>

              <div className="p-4 bg-green-50 rounded-lg">
                <h4 className="font-semibold text-green-900 mb-2">
                  ✅ {quadrantAnalysis.q1_champions.length} Champion Districts for Best Practice Replication
                </h4>
                <p className="text-sm text-green-800">
                  Action: Document and share success strategies across state
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Header */}
          <div className="mb-8 pt-12">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Aadhaar & FRS Authentication Dashboard
            </h1>
            <p className="text-gray-600">
              Comprehensive monitoring of Aadhaar Face Recognition System and THR distribution authentication
            </p>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid w-full grid-cols-2 md:grid-cols-4">
              <TabsTrigger value="overview">State Overview</TabsTrigger>
              <TabsTrigger value="frs-dive">FRS Deep Dive</TabsTrigger>
              <TabsTrigger value="kit-performance">Kit Performance</TabsTrigger>
              <TabsTrigger value="red-flags">Red Flags</TabsTrigger>
            </TabsList>

            <TabsContent value="overview">
              <OverviewTab />
            </TabsContent>

            <TabsContent value="frs-dive">
              <FRSDeepDiveTab />
            </TabsContent>

            <TabsContent value="kit-performance">
              <KitPerformanceTab />
            </TabsContent>

            <TabsContent value="red-flags">
              <RedFlagTab />
            </TabsContent>
          </Tabs>

        </div>
      </div>

      {/* District Detail Modal */}
      <THRDistrictDetailModal
        district={selectedDistrict}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedDistrict(null);
        }}
        reportingPeriod="Oct-2025"
        dashboardType="frs"
      />
    </div>
  );
};

export default FRSAuthDashboard;
